/**
 * NotificationToast
 *
 * Global listener component that displays toast notifications for
 * high-priority real-time notifications (escalation, crisis_alert,
 * publish_failed). Mounted at the app root level.
 *
 * Listens for custom 'mellify:notification-toast' events dispatched
 * by the useNotificationStream hook.
 */

import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Shield, XCircle, X } from 'lucide-react';
import { cn } from '@mellify/ui';
import { AnimatePresence, motion } from 'framer-motion';
import type { Notification } from '@mellify/shared';

// ─── Toast State ────────────────────────────────────────────────────────────

interface ToastItem {
  id: string;
  notification: Notification;
  timestamp: number;
}

/** Auto-dismiss delay in milliseconds (8 seconds for high-priority) */
const TOAST_DURATION = 8_000;

/** Maximum number of simultaneous toasts */
const MAX_TOASTS = 3;

/** Safely coerce a value to a renderable string */
function safeString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

/**
 * Get icon and color for high-priority notification types.
 */
function getToastConfig(type: string): {
  icon: React.ReactNode;
  borderClass: string;
  iconBgClass: string;
} {
  switch (type) {
    case 'escalation':
      return {
        icon: <AlertTriangle className="w-5 h-5 text-warning-500" />,
        borderClass: 'border-l-warning-500',
        iconBgClass: 'bg-warning-50 dark:bg-warning-500/10',
      };
    case 'crisis_alert':
      return {
        icon: <Shield className="w-5 h-5 text-danger-500" />,
        borderClass: 'border-l-danger-500',
        iconBgClass: 'bg-danger-50 dark:bg-danger-500/10',
      };
    case 'publish_failed':
      return {
        icon: <XCircle className="w-5 h-5 text-danger-500" />,
        borderClass: 'border-l-danger-500',
        iconBgClass: 'bg-danger-50 dark:bg-danger-500/10',
      };
    default:
      return {
        icon: <AlertTriangle className="w-5 h-5 text-primary-500" />,
        borderClass: 'border-l-primary-500',
        iconBgClass: 'bg-primary-50 dark:bg-primary-500/10',
      };
  }
}

export const NotificationToast: React.FC = () => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const navigate = useNavigate();

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (notification: Notification) => {
      const newToast: ToastItem = {
        id: `${notification.id}-${Date.now()}`,
        notification,
        timestamp: Date.now(),
      };

      setToasts((prev) => {
        const updated = [newToast, ...prev];
        // Limit to MAX_TOASTS
        return updated.slice(0, MAX_TOASTS);
      });

      // Auto-dismiss after TOAST_DURATION
      setTimeout(() => {
        removeToast(newToast.id);
      }, TOAST_DURATION);
    },
    [removeToast]
  );

  // Listen for custom notification toast events
  useEffect(() => {
    const handleToastEvent = (event: Event) => {
      const customEvent = event as CustomEvent<Notification>;
      if (customEvent.detail) {
        addToast(customEvent.detail);
      }
    };

    window.addEventListener(
      'mellify:notification-toast',
      handleToastEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        'mellify:notification-toast',
        handleToastEvent as EventListener
      );
    };
  }, [addToast]);

  const handleToastClick = (toast: ToastItem) => {
    const actionUrl = safeString(toast.notification.actionUrl);
    if (actionUrl) {
      navigate(actionUrl);
    }
    removeToast(toast.id);
  };

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 pointer-events-none"
      aria-live="polite"
      aria-label="Notification toasts"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => {
          const notificationType = safeString(toast.notification.type);
          const config = getToastConfig(notificationType);
          const title = safeString(toast.notification.title, 'Notification');
          const message = safeString(toast.notification.message);
          const actionUrl = safeString(toast.notification.actionUrl);

          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 100, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="pointer-events-auto"
            >
              <div
                onClick={() => handleToastClick(toast)}
                role="alert"
                className={cn(
                  'w-[380px] max-w-[calc(100vw-2rem)] bg-white dark:bg-dark-900 border border-dark-200 dark:border-dark-700 rounded-xl shadow-2xl cursor-pointer',
                  'border-l-4',
                  config.borderClass,
                  'hover:shadow-3xl transition-shadow duration-200'
                )}
              >
                <div className="flex items-start gap-3 p-4">
                  {/* Icon */}
                  <div
                    className={cn(
                      'shrink-0 flex items-center justify-center w-10 h-10 rounded-lg',
                      config.iconBgClass
                    )}
                  >
                    {config.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-dark-900 dark:text-dark-100 leading-tight">
                      {title}
                    </h4>
                    <p className="text-xs text-dark-500 dark:text-dark-400 mt-1 line-clamp-2 leading-relaxed">
                      {message}
                    </p>
                    {actionUrl && (
                      <p className="text-xs text-primary-500 font-medium mt-1.5">
                        Click to view details →
                      </p>
                    )}
                  </div>

                  {/* Close button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeToast(toast.id);
                    }}
                    className="shrink-0 p-1 rounded-lg text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
                    aria-label="Dismiss notification"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Progress bar for auto-dismiss */}
                <div className="h-1 bg-dark-100 dark:bg-dark-800 rounded-b-xl overflow-hidden">
                  <motion.div
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: TOAST_DURATION / 1000, ease: 'linear' }}
                    className={cn(
                      'h-full',
                      notificationType === 'crisis_alert' || notificationType === 'publish_failed'
                        ? 'bg-danger-500'
                        : 'bg-warning-500'
                    )}
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

NotificationToast.displayName = 'NotificationToast';